/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import mapProps from 'js/lib/mapProps';

import { Grid, Link, LogoWrapper } from '@coursera/cds-core';

import withFragments from 'bundles/graphql/components/withFragments';
import Icon from 'bundles/iconfont/Icon';
import PartnersV1 from 'bundles/naptimejs/resources/partners.v1';
import type { PartnerPageQuery_PartnersV1Resource_shortName_partners as Partner } from 'bundles/partners-graphql/components/__generated__/PartnerPageQuery';

const partnerLinksStyles = {
  partnerLink: css`
    margin-bottom: '30px';

    svg,
    a > span {
      i {
        vertical-align: middle;
      }
    }

    .link-text {
      padding: 10px;
      display: inline-block;
      line-height: '30px';
      margin-left: '10px';
    }

    a > span > i,
    a .icon-wrapper i {
      font-size: 30px;
      color: rgb(0 0 0 / 87%);
    }

    .link-col {
      min-width: 25%;
      padding: 0 15px;
    }

    .icon-wrapper {
      display: inline-block;
      line-height: 0;
      vertical-align: middle;
    }
  `,
};

type PropsFromCaller = {
  partner: Partner;
};

type PropsToComponent = {
  partner: PartnersV1;
};

export const PartnerLinks = ({ partner }: PropsToComponent) => {
  return (
    <div css={partnerLinksStyles.partnerLink} className="bt3-row">
      <Grid container className="horizontal-box">
        {partner.links?.website && (
          <Grid className="link-col">
            <Link
              href={partner.links.website}
              variant="standard"
              typographyVariant="bodyPrimary"
              icon={
                <span className="icon-wrapper">
                  <LogoWrapper paddingVariant="none">
                    <Icon name="globe" />
                  </LogoWrapper>
                </span>
              }
              iconPosition="before"
              target="_blank"
            >
              <span className="link-text">{partner.prettyWebsiteUrl}</span>
            </Link>
          </Grid>
        )}

        {partner.links?.facebook && (
          <Grid className="link-col">
            <Link
              href={`https://www.facebook.com/${partner.links.facebook}`}
              variant="standard"
              typographyVariant="bodyPrimary"
              icon={
                <span className="icon-wrapper">
                  <LogoWrapper paddingVariant="none">
                    <Icon name="facebook-official" />
                  </LogoWrapper>
                </span>
              }
              iconPosition="before"
              target="_blank"
            >
              <span className="link-text">{partner.links.facebook}</span>
            </Link>
          </Grid>
        )}

        {partner.links?.twitter && (
          <Grid className="link-col">
            <Link
              href={`https://www.twitter.com/${partner.links.twitter}`}
              variant="standard"
              typographyVariant="bodyPrimary"
              icon={
                <span className="icon-wrapper">
                  <LogoWrapper paddingVariant="none">
                    <Icon name="twitter" />
                  </LogoWrapper>
                </span>
              }
              iconPosition="before"
              target="_blank"
            >
              <span className="link-text">{partner.links.twitter}</span>
            </Link>
          </Grid>
        )}

        {partner.links?.youtube && (
          <Grid className="link-col">
            <Link
              href={`https://www.youtube.com/${partner.links.youtube}`}
              variant="standard"
              typographyVariant="bodyPrimary"
              icon={
                <span className="icon-wrapper">
                  <LogoWrapper paddingVariant="none">
                    <svg width="30" height="30" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1047 1303v-157q0-50-29-50-17 0-33 16v224q16 16 33 16 29 0 29-49zm184-122h66v-34q0-51-33-51t-33 51v34zm-571-266v70h-80v423h-74v-423h-78v-70h232zm201 126v367h-67v-40q-39 45-76 45-33 0-42-28-6-16-6-54v-290h66v270q0 24 1 26 1 15 15 15 20 0 42-31v-280h67zm252 111v146q0 52-7 73-12 42-53 42-35 0-68-41v36h-67v-493h67v161q32-40 68-40 41 0 53 42 7 21 7 74zm251 129v9q0 29-2 43-3 22-15 40-27 40-80 40-52 0-81-38-21-27-21-86v-129q0-59 20-86 29-38 80-38t78 38q21 28 21 86v76h-133v65q0 51 34 51 24 0 30-26 0-1 .5-7t.5-16.5v-21.5h68zm-451-824v156q0 51-32 51t-32-51v-156q0-52 32-52t32 52zm533 713q0-177-19-260-10-44-43-73.5t-76-34.5q-136-15-412-15-275 0-411 15-44 5-76.5 34.5t-42.5 73.5q-20 87-20 260 0 176 20 260 10 43 42.5 73t75.5 35q137 15 412 15t412-15q43-5 75.5-35t42.5-73q20-84 20-260zm-755-651l90-296h-75l-51 195-53-195h-78l24 69 23 69q35 103 46 158v201h74v-201zm289 81v-130q0-58-21-87-29-38-78-38-51 0-78 38-21 29-21 87v130q0 58 21 87 27 38 78 38 49 0 78-38 21-27 21-87zm181 120h67v-370h-67v283q-22 31-42 31-15 0-16-16-1-2-1-26v-272h-67v293q0 37 6 55 11 27 43 27 36 0 77-45v40zm503-304v960q0 119-84.5 203.5t-203.5 84.5h-960q-119 0-203.5-84.5t-84.5-203.5v-960q0-119 84.5-203.5t203.5-84.5h960q119 0 203.5 84.5t84.5 203.5z" />
                    </svg>
                  </LogoWrapper>
                </span>
              }
              iconPosition="before"
              target="_blank"
            >
              <span className="link-text">{partner.links.youtube}</span>
            </Link>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export const partnerLinksFragment = gql`
  fragment PartnerLinksFragment on PartnersV1 {
    links {
      website
      twitter
      facebook
      youtube
    }
  }
`;

export default compose<PropsToComponent, PropsFromCaller>(
  withFragments({
    partner: partnerLinksFragment,
  }),
  mapProps(({ partner }: PropsFromCaller) => ({
    partner: new PartnersV1(partner),
  }))
)(PartnerLinks);
